'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

import { Box, cn, cssVar, Flex, Link } from '@zoomit/primitives';
import type { MenuData } from '@zoomit/web-kit';
import { IconRenderer } from '@zoomit/web-kit';
import { Anchor, theme, Typography } from '@zoomit/zoomit-ds';

import { canDisplayMenuItem } from '../../../helpers/menu/canDisplayMenuItem';
import { getNestedIndicatorButtons } from '../../../helpers/menu/getNestedIndecatorButtons';
import { HeightMotion } from '../../shared/HeightMotion';
import { MenuSubItems } from './MenuSubItems';
import { MyProfileSection } from './MyProfileSection';

type Props = {
  menuData: MenuData;
};

export const MenuItems = (props: Props) => {
  const { menuData } = props;

  const [triggeredIdx, setTriggeredIdx] = useState(-1);

  return (
    <Flex
      justifyContent={'space-between'}
      onPointerLeave={() => {
        setTriggeredIdx(-1);
      }}
      gap={3.6}
    >
      <div onPointerEnter={() => setTriggeredIdx(-1)}>
        <MyProfileSection />
      </div>

      {menuData.items.map((item, idx, items) => {
        const indicatorButtons = getNestedIndicatorButtons(item);
        const Comp = item.url ? Link : 'div';
        return (
          <Flex
            flexDirection={'column'}
            alignItems={'center'}
            gap={0.4}
            justifyContent={'end'}
            key={idx}
            className={cn(
              'xxl:flex relative',
              idx > items.length - 5 && 'lg:hidden xl:flex',
              idx > items.length - 3 && 'xl:hidden',
            )}
            onPointerOver={() => setTriggeredIdx(idx)}
          >
            <Comp
              href={item.url}
              rel={item.noFollow ? 'nofollow' : ''}
              className={'flex h-full flex-col items-center justify-between gap-1'}
            >
              {canDisplayMenuItem.renderIcon(item.displayType) && item.icon && (
                <IconRenderer icon={item.icon.content} fill={cssVar(theme.primary1)} />
              )}
              <Typography
                className={'cursor-pointer'}
                lineHeight={'26px'}
                fontWeight={700}
                textColor={triggeredIdx === idx ? cssVar(theme.primary1) : cssVar(theme.text)}
              >
                {item.title}
              </Typography>
            </Comp>

            <AnimatePresence>
              {triggeredIdx === idx && item.children && item.children.length && (
                <motion.div
                  initial={{ opacity: 0 }}
                  exit={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                  className={
                    'bg-card shadow-main absolute top-14 right-0 z-50 w-fit origin-left translate-x-20 rounded-sm py-4 whitespace-nowrap'
                  }
                >
                  <HeightMotion>
                    <MenuSubItems key={triggeredIdx} items={item.children} />
                    {indicatorButtons.length > 0 && (
                      <Flex className={'border-borderPrimary mt-4 border-t px-6 pt-4'} flexWrap={'nowrap'}>
                        {indicatorButtons.map((button, buttonIdx) => {
                          if (!button) return null;
                          return (
                            <Box key={buttonIdx}>
                              <Anchor
                                className={'!text-body transition'}
                                href={button.url}
                                rel={button.nofollow ? 'nofollow' : ''}
                                variant={'text'}
                                rightIcon={button.icon && <IconRenderer icon={button.icon.content} />}
                              >
                                {button.title}
                              </Anchor>
                            </Box>
                          );
                        })}
                      </Flex>
                    )}
                  </HeightMotion>
                </motion.div>
              )}
            </AnimatePresence>
          </Flex>
        );
      })}
    </Flex>
  );
};
